import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { authService } from '../../services/authService';

const AdminLoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    return password?.length >= 8;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (authError) {
      setAuthError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData?.password)) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setAuthError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await authService?.signIn(formData?.email, formData?.password);
      onClose();
      navigate('/admin-dashboard');
    } catch (error) {
      setAuthError(error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in"
      style={{ background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-md mx-4 rounded-xl shadow-lg animate-scale-in" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
          style={{ background: 'transparent' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          aria-label="Close modal"
        >
          <Icon name="X" size={20} color="var(--color-muted-foreground)" />
        </button>

        <div className="p-6 md:p-8 lg:p-10">
          <div className="flex flex-col items-center mb-6 md:mb-8">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(245, 203, 92, 0.1)' }}>
              <Icon name="Shield" size={32} color="var(--color-secondary)" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-2" style={{ color: 'var(--color-text-primary)', fontFamily: 'IBM Plex Sans', fontWeight: 600 }}>
              Admin Access
            </h2>
            <p className="text-sm md:text-base text-center" style={{ color: 'var(--color-text-secondary)', fontFamily: 'IBM Plex Sans' }}>
              Secure authentication for content management
            </p>
          </div>

          {authError && (
            <div className="mb-6 p-4 rounded-lg flex items-start gap-3" style={{ background: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.2)' }}>
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm" style={{ color: 'var(--color-destructive)' }}>{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="email@example.com"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="secondary"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName="LogIn"
              iconPosition="right"
              className="mt-6 md:mt-8"
            >
              {isLoading ? 'Authenticating...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginModal;